import {
  LayoutDashboard,
  Building2,
  Users,
  BarChart3,
  MessageSquare,
  Bot,
  Settings,
  HelpCircle,
  ChevronRight,
  Clock,
  UserCheck,
  TestTube,
  Activity,
  CreditCard,
  Menu,
  X,
  Globe
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  userRole: 'super_admin' | 'tenant_admin' | 'tenant_manager' | 'viewer';
  isImpersonating?: boolean;
  impersonatedUserRole?: string;
}

const navigationItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin', 'tenant_admin', 'tenant_manager', 'viewer'] },
  { id: 'tenants', label: 'Tenants', icon: Building2, roles: ['super_admin'] },
  { id: 'users', label: 'Users', icon: Users, roles: ['super_admin', 'tenant_admin', 'tenant_manager', 'viewer'] },
  { id: 'queue-manager', label: 'Queue Manager', icon: Clock, roles: ['super_admin', 'tenant_admin', 'tenant_manager'] },
  { id: 'reports', label: 'Reports', icon: BarChart3, roles: ['super_admin', 'tenant_admin', 'tenant_manager', 'viewer'] },
  { id: 'customers', label: 'Customers', icon: UserCheck, roles: ['super_admin', 'tenant_admin', 'tenant_manager', 'viewer'] },
  { id: 'messaging', label: 'Messaging', icon: MessageSquare, roles: ['super_admin', 'tenant_admin'] },
  { id: 'queueai', label: 'QueueAI', icon: Bot, roles: ['super_admin', 'tenant_admin'], premium: true },
  { id: 'billing', label: 'Billing', icon: CreditCard, roles: ['tenant_admin'] },
  { id: 'settings', label: 'Settings', icon: Settings, roles: ['super_admin', 'tenant_admin'] },
  { 
    id: 'system-tests', 
    label: 'System Tests', 
    icon: TestTube, 
    roles: ['super_admin'],
    children: [
      { id: 'sample-data-generator', label: 'Sample Data Generator' }
    ]
  },
  { id: 'system-status', label: 'System Status', icon: Activity, roles: ['super_admin'] },
  { id: 'sms-countries', label: 'SMS Countries', icon: Globe, roles: ['super_admin'] },
  { id: 'help', label: 'Help', icon: HelpCircle, roles: ['super_admin', 'tenant_admin', 'tenant_manager', 'viewer'] },
];

export function Sidebar({ activeTab, onTabChange, userRole, isImpersonating, impersonatedUserRole }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // When impersonating, use the impersonated user's role for filtering
  const effectiveRole = isImpersonating && impersonatedUserRole ? 
    impersonatedUserRole.toLowerCase().replace('admin', 'tenant_admin').replace('manager', 'tenant_manager') : 
    userRole;
    
  const filteredItems = navigationItems.filter(item => 
    item.roles.includes(effectiveRole)
  );

  return (
    <>
      {/* Mobile toggle button - positioned in content area */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          className="fixed bottom-6 right-6 z-50 lg:hidden bg-primary text-white border-2 border-primary rounded-full p-3 shadow-2xl hover:bg-primary/90 transition-all"
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </button>
      )}

      {/* Overlay for mobile */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:relative top-0 bottom-0 left-0 z-40 w-64 border-r bg-white transition-transform duration-300 ease-in-out overflow-y-auto",
        isCollapsed ? "-translate-x-full lg:translate-x-0" : "translate-x-0"
      )}>
      <nav className="p-4 space-y-2">
        {filteredItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => {
                onTabChange(item.id);
                // Auto-close on mobile after selection
                if (window.innerWidth < 1024) {
                  setIsCollapsed(true);
                }
              }}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors",
                isActive 
                  ? "bg-primary text-primary-foreground" 
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <div className="flex items-center space-x-3">
                <Icon className={cn(
                  "h-4 w-4",
                  item.premium ? "text-purple-600" : ""
                )} />
                <span className="flex items-center gap-2">
                  {item.label}
                  {item.premium && (
                    <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                      AI
                    </span>
                  )}
                </span>
              </div>
              <ChevronRight className={cn(
                "h-4 w-4 transition-transform",
                isActive ? "rotate-90" : ""
              )} />
            </button>
          );
        })}
      </nav>
      
      {/* Close button inside sidebar for mobile */}
      {!isCollapsed && (
        <div className="lg:hidden p-4 border-t">
          <button
            onClick={() => setIsCollapsed(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            <X className="h-4 w-4" />
            <span className="text-sm font-medium">Close Menu</span>
          </button>
        </div>
      )}
    </aside>
    </>
  );
}